// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { useCallback, useEffect, useRef } from 'react';
import { useChatStore } from '../stores/chatStore';
import { useShellStore } from '../stores/shellStore';
import type { AlarmContextSeed, AskHeliosMessage } from '../types';

/**
 * Starter prompts for the empty state, shown only once a site is selected.
 * Each one lands inside the advisory boundary, so a first-time operator learns
 * what Ask Helios answers by using it rather than by hitting a refusal.
 */
export const SITE_SUGGESTED_QUESTIONS = [
  'What alarms are currently active at this site?',
  'How many PDUs are installed at this site?',
  'What anomalies are currently active at this site?',
];

export interface UseAskHeliosReturn {
  messages: AskHeliosMessage[];
  inputValue: string;
  setInputValue: (_value: string) => void;
  isThinking: boolean;
  /** Sanitized progress label while a tool runs (e.g. "Searching the SOP library…"); null otherwise. */
  progressLabel: string | null;
  sessionId: string | undefined;
  /** Product conversation id for the active thread; undefined until the first answer. */
  conversationId: string | undefined;
  /** True when the last answer could not be saved — a non-blocking indicator. */
  unsaved: boolean;
  /** True while a stored conversation is being hydrated. */
  isLoadingConversation: boolean;
  messagesEndRef: React.RefObject<HTMLDivElement | null>;
  handleSendMessage: (_e: React.FormEvent | React.KeyboardEvent) => void;
  handleNewChat: () => void;
  /** Aborts the in-flight answer and offers a retry. */
  stop: () => void;
  /** Replaces the on-screen thread with a stored conversation (resume / replay). */
  resumeConversation: (_conversationId: string) => Promise<void>;
  /** True while only the welcome turn is on screen — drives the starter prompts. */
  isEmpty: boolean;
  /** Sends `question` directly, bypassing the input box (starter prompt / retry). */
  askQuestion: (_question: string) => void;
  /** The last question that failed, so the error turn can offer a retry. */
  retryableQuestion: string | null;
  /** The alarm this thread is scoped to, shown as a removable context chip. */
  threadSeed: AlarmContextSeed | null;
  /** Whether `threadSeed` has already gone out on a request. */
  contextSeedSent: boolean;
  /** The alarm on screen right now, if any — offered as an addable context chip. */
  pageAlarmContext: AlarmContextSeed | null;
  /** Scopes the thread to `seed` without resetting it (the chip's "+"). */
  attachContext: (_seed: AlarmContextSeed) => void;
  /** Drops the thread's alarm context (the chip's "x"). */
  removeContext: () => void;
}

/**
 * View of the Ask Helios conversation for the chat surface.
 *
 * The conversation itself lives in `stores/chatStore` so it survives navigation
 * and the panel closing; this hook only binds it to a component — the scroll
 * anchor, the form-submit wrapper, and the derived empty state.
 */
export function useAskHelios(): UseAskHeliosReturn {
  const messages = useChatStore(s => s.messages);
  const inputValue = useChatStore(s => s.inputValue);
  const isThinking = useChatStore(s => s.isThinking);
  const progressLabel = useChatStore(s => s.progressLabel);
  const sessionId = useChatStore(s => s.sessionId);
  const conversationId = useChatStore(s => s.conversationId);
  const unsaved = useChatStore(s => s.unsaved);
  const isLoadingConversation = useChatStore(s => s.isLoadingConversation);
  const retryableQuestion = useChatStore(s => s.retryableQuestion);
  const threadSeed = useChatStore(s => s.threadSeed);
  const contextSeedSent = useChatStore(s => s.contextSeedSent);
  const setInputValue = useChatStore(s => s.setInputValue);
  const askQuestion = useChatStore(s => s.ask);
  const handleNewChat = useChatStore(s => s.newChat);
  const stop = useChatStore(s => s.stop);
  const resumeConversation = useChatStore(s => s.resumeConversation);
  const attachContext = useChatStore(s => s.attachSeed);
  const removeContext = useChatStore(s => s.clearThreadSeed);
  const pageAlarmContext = useShellStore(s => s.pageAlarmContext);

  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  const handleSendMessage = useCallback(
    (e: React.FormEvent | React.KeyboardEvent) => {
      e.preventDefault();
      // `isLoadingConversation` too: a resume leaves isThinking false while it
      // hydrates, so this is the window where a send would have silently
      // discarded the conversation being resumed. The store guards it as well —
      // this keeps the input from clearing on a submit that will not be sent.
      if (!inputValue.trim() || isThinking || isLoadingConversation) return;
      askQuestion(inputValue);
      setInputValue('');
    },
    [inputValue, isThinking, isLoadingConversation, askQuestion, setInputValue]
  );

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isThinking]);

  return {
    messages,
    inputValue,
    setInputValue,
    isThinking,
    progressLabel,
    sessionId,
    conversationId,
    unsaved,
    isLoadingConversation,
    messagesEndRef,
    handleSendMessage,
    handleNewChat,
    stop,
    resumeConversation,
    isEmpty: messages.length <= 1,
    askQuestion,
    retryableQuestion,
    threadSeed,
    contextSeedSent,
    pageAlarmContext,
    attachContext,
    removeContext,
  };
}
